import type { DatabaseMaterial } from '@/hooks/useMaterials';
import { getScrewPricePerPiece } from '@/utils/screwPricing';

/**
 * Fissaggi delle guide: tasselli e chiodi.
 *
 * Le guide vanno fissate a pavimento e a soffitto — forando e tassellando,
 * oppure sparando il chiodo con l'inchiodatrice. Sono due modi alternativi di
 * fare la stessa cosa: si contano allo stesso modo e stanno nella stessa lista.
 *
 * Il passo e' fisso a 50 cm: e' la pratica di cantiere e il valore piu'
 * cautelativo fra le fonti (un documento legato alla UNI 11424 indica 50 cm, i
 * manuali di posa 50-60 cm, la scheda tecnica Knauf W11 ammette fino a 100 cm).
 *
 * L'INCIDENZA NON SI SCRIVE A MANO: si ricava dalla quantita' di guida.
 * Ogni metro lineare di guida porta 2 tasselli, e siccome per ogni m² di parete
 * ci sono `2 / H` ml di guida (una a pavimento e una a soffitto), i tasselli al
 * m² valgono `4 / H` — 1,48 a H = 2,7 m. Per metro lineare di PARETE sono
 * sempre 4, a qualunque altezza: e' l'invariante che ci si aspetta in cantiere.
 *
 * Legandola alla quantita' di guida invece di scriverla come costante, i
 * tasselli restano coerenti con le guide in ogni motore di calcolo, anche se
 * un domani l'altezza smettera' di essere convenzionale.
 */

/** Passo di tassellatura delle guide, in mm. */
export const ANCHOR_SPACING_MM = 500;

/** Fissaggi per metro lineare di guida (2 a passo 50 cm). */
export const ANCHORS_PER_METRE_OF_GUIDE = 1000 / ANCHOR_SPACING_MM;

/** Würth SHARK HAMMER 6x40 — il tassello effettivamente acquistato. */
export const DEFAULT_ANCHOR_CODE = '590630640';

/**
 * Fissaggi al m² di parete, dati i metri lineari di guida al m².
 * Unica formula: la usano il motore costi e il riepilogo materiali.
 */
export function anchorQuantityPerSqm(guideMlPerSqm: number): number {
  const ml = Number(guideMlPerSqm) || 0;
  if (ml <= 0) return 0;
  return ml * ANCHORS_PER_METRE_OF_GUIDE;
}

/**
 * Come si riconosce un fissaggio da guida.
 *
 * Non sono solo tasselli: a volte la guida si SPARA a pavimento con
 * l'inchiodatrice invece di forarla e tassellarla. Chiodo e tassello sono
 * alternativi, si contano allo stesso modo (uno ogni 50 cm) e stanno quindi
 * nella stessa lista.
 */
const FASTENER_WORDS = /(tassell|fischer|chiodo|chiodi)/i;

/**
 * "Punta chiodo 25" NON e' un chiodo: e' una VITE con punta a chiodo (needle
 * point, per lamiere sottili). Il catalogo Saint-Gobain la classifica come
 * `accessory` invece che `screw`, quindi il gate di categoria non basta.
 *
 * La regola e' posizionale: quando il nome APRE con "punta", la parola che
 * segue descrive la punta del prodotto, non il prodotto. Vale sia per le viti
 * ("Punta chiodo") sia per gli utensili ("Punta trapano").
 */
const SCREW_TIP_PATTERN = /^\s*punta\b/i;

/**
 * Attrezzi che finiscono in catalogo fra gli accessori ma non sono fissaggi.
 *
 * NB su "punta": in questo catalogo indica quasi sempre il TIPO di punta del
 * fissaggio ("Punta chiodo 25", scatola da 1000 a 10,66 €), non un trapano.
 * Va quindi esclusa solo quando è davvero un utensile — "Punta trapano 25" —
 * altrimenti si buttano via i chiodi Saint-Gobain.
 */
const TOOL_WORDS = /(\bpinza\b|\butensile\b|attrezz|\binserto\b|\bchiave\b|spatola|inchiodatrice|sparachiodi|\bpistola\b|punta\s+(trapano|widia|sds|elicoidale))/i;

/**
 * Parole che, nel nome di un TASSELLO, indicano una confezione e non un pezzo.
 *
 * Serve perche' il listino Knauf contiene voci come "Tassello a percussione
 * IN BARATTOLO ... 19,12 €" marcate `unit='pz'`: quei 19 € sono il prezzo del
 * barattolo, non del singolo tassello. Il dato si contraddice da solo.
 *
 * Il filtro vale solo dentro `anchorPricePerPiece`, che gira gia' sui soli
 * tasselli: fuori di qui "scatola" e' spesso un prodotto vero (le scatole di
 * derivazione elettriche si vendono davvero a pezzo).
 */
const PACKAGING_WORDS = /(barattol|secchi|blister|confezione|scatola)/i;

/**
 * E' un fissaggio da guida — tassello o chiodo — e non un attrezzo.
 *
 * Il gate su `category === 'accessory'` tiene fuori le "Viti punta a chiodo",
 * che sono `category='screw'` e servono a fissare le lastre, non le guide.
 */
export function isAnchorMaterial(m: DatabaseMaterial | null | undefined): boolean {
  if (!m || m.category !== 'accessory') return false;
  const name = m.name ?? '';
  if (!FASTENER_WORDS.test(name)) return false;
  if (SCREW_TIP_PATTERN.test(name)) return false;
  return !TOOL_WORDS.test(name);
}

/**
 * Prezzo al pezzo, oppure `null` se non e' ricavabile.
 *
 * QUESTA E' LA PROTEZIONE PRINCIPALE. In catalogo 30 tasselli Knauf hanno
 * `unit='scatola'` con `box_pieces` VUOTO: leggere quei 10,48 €/scatola come
 * prezzo del pezzo darebbe ~15,50 €/m² di soli tasselli, cioe' ~10.900 €
 * inventati su una parete da 700 m². E' lo stesso meccanismo del bug PV017
 * sulle viti. Un tassello senza prezzo/pezzo derivabile non e' selezionabile.
 */
export function anchorPricePerPiece(m: DatabaseMaterial | null | undefined): number | null {
  if (!m) return null;
  const unit = String(m.unit ?? '').toLowerCase().trim();
  const box = Number(m.box_pieces ?? 0);
  // Venduto a confezione ma senza sapere quanti pezzi ci sono dentro: inutilizzabile.
  if (unit !== 'pz' && !(box > 0)) return null;
  // Dice "pz" ma il nome dichiara una confezione: il dato si contraddice, e a
  // fidarsi si prende il prezzo del barattolo per quello del tassello (19 €
  // invece di ~0,19). Si esclude invece di indovinare quanti pezzi contiene.
  if (unit === 'pz' && !(box > 0) && PACKAGING_WORDS.test(m.name ?? '')) return null;
  const price = getScrewPricePerPiece(m);
  return price > 0 ? price : null;
}

/** I fissaggi che si possono davvero usare, dal piu' economico al piu' caro. */
export function listUsableAnchors(
  catalog: DatabaseMaterial[],
  hiddenIds?: Set<string>,
): DatabaseMaterial[] {
  return catalog
    .filter(m => isAnchorMaterial(m) && !hiddenIds?.has(m.id) && anchorPricePerPiece(m) != null)
    .sort((a, b) => (anchorPricePerPiece(a) ?? 0) - (anchorPricePerPiece(b) ?? 0));
}

/**
 * Fissaggio predefinito: il tassello Würth se c'e', altrimenti il piu'
 * economico fra quelli utilizzabili. Scelta deterministica e spiegabile — non
 * un match per somiglianza, che e' esattamente cio' che abbiamo tolto dalle
 * guide. Il chiodo a sparo resta un'alternativa da scegliere a mano.
 */
export function pickDefaultAnchor(
  catalog: DatabaseMaterial[],
  hiddenIds?: Set<string>,
): DatabaseMaterial | null {
  const usable = listUsableAnchors(catalog, hiddenIds);
  return usable.find(m => m.code === DEFAULT_ANCHOR_CODE) ?? usable[0] ?? null;
}
